import {
  Box,
  Button,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import set from "lodash/set";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { useNavigate, useParams } from "react-router-dom";
import {
  useGetOneProductQuery,
  useAddProductsMutation,
  useUpdateProductsMutation,
} from "slices/eCommerce/shopProducts";

import Basic from "./basic";
import Variations from "./variations";
import Seo from "./seo";
import { useState } from "react";
import TagSelection from "./tagSelection";

// Step titles
const steps = ["Basic Info", "Variations", "Tags", "SEO"];

// Validation schemas per step
const validationSchemas = [
  Yup.object({
    category: Yup.string().required("Category is required"),
    productFor: Yup.string().required("Product type is required"),
    subCategory: Yup.string().required("Subcategory is required"),
    brand: Yup.string().required("Brand is required"),
    name: Yup.string().required("Name is required"),
    description: Yup.string().required("Description is required"),
  }),
  Yup.object({
    variations: Yup.array()
      .min(1, "At least one variation is required")
      .of(
        Yup.object().shape({
          // color: Yup.string().required("Color is required"),
          size: Yup.string().required("Size is required"),
          price: Yup.number()
            .typeError("Price must be a number")
            .required("Price is required"),
          stock: Yup.number()
            .typeError("Stock must be a number")
            .required("Stock is required"),
          sku: Yup.number()
            .typeError("SKU must be a number")
            .required("SKU is required"),
          discount: Yup.number()
            .typeError("Discount must be a number")
            .required("Discount is required"),
          media: Yup.array().min(1, "At least one media file is required"),
        })
      ),
  }),
  Yup.object({
    suitableFor: Yup.object().shape({
      gender: Yup.array().of(Yup.string()),
      minAge: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        )
        .min(18, "Min age must be at least 18")
        .notRequired(),
      maxAge: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value
        )
        .max(60, "Max age must be 60 or less")
        .when("minAge", (minAge, schema) =>
          minAge != null
            ? schema.moreThan(minAge, "Max age must be greater than min age")
            : schema
        )
        .notRequired(),
    }),
  }),

  Yup.object({
    metaTitle: Yup.string().required("Meta title is required"),
    metaDescription: Yup.string().required("Meta description is required"),
    keywords: Yup.array().min(1, "At least one keyword is required"),
  }),
];

function AddProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: initialData, isLoading } = useGetOneProductQuery(id, {
    skip: !isEdit,
  });

  const [add] = useAddProductsMutation();
  const [update] = useUpdateProductsMutation();

  const [activeStep, setActiveStep] = useState(0);

  const initialValues = {
    productFor: "",
    category: "",
    subCategory: "",
    brand: "",
    name: "",
    description: "",
    uses_count: 0,
    thumbnail: "",
    isAvailable: true,
    trending: true,
    metaTitle: "",
    metaDescription: "",
    keywords: [],
    variations: [],
    //
    skinTypes: [],
    concerns: [],
    tags: [],
    suitableFor: {
      gender: [],
      minAge: "",
      maxAge: "",
    },

    ...(initialData?.data || {}),
  };

  const handleNext = async (e, values, actions) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      await validationSchemas[activeStep].validate(values, {
        abortEarly: false,
      });
      setActiveStep((prev) => prev + 1);
    } catch (err) {
      if (err.inner) {
        const formErrors = {};
        const touchedFields = {};
        err.inner.forEach((e) => {
          set(formErrors, e.path, e.message);
          set(touchedFields, e.path, true);
        });
        actions.setErrors(formErrors);
        actions.setTouched(touchedFields);
      }
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Handle step click - only in edit mode, all steps are clickable
  const handleStepClick = (stepIndex) => {
    if (isEdit) {
      setActiveStep(stepIndex);
    }
  };

  const handleSubmit = async (values, actions) => {
    console.log("values", values);
    try {
      if (isEdit) {
        await update({ id, body: values }).unwrap();
        navigate("/e-commerce/products", { state: { refresh: true } });
      } else {
        await add(values).unwrap();
        navigate("/e-commerce/products", { state: { refresh: true } });
      }
    } catch (err) {
      console.error(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          {isEdit ? "Edit Product" : "Add Product"}
        </Typography>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step 
                key={label}
                onClick={() => handleStepClick(index)}
                sx={{ 
                  cursor: isEdit ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: isEdit ? 'action.hover' : 'transparent',
                    borderRadius: 1,
                  }
                }}
              >
                <StepLabel 
                  sx={{
                    cursor: isEdit ? 'pointer' : 'default',
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {(!isEdit || (isEdit && initialData)) && (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchemas[activeStep]}
              onSubmit={handleSubmit}
              enableReinitialize={isEdit}
            >
              {(formik) => (
                <Form>
                  {/* Render step components */}
                  {activeStep === 0 && (
                    <Basic
                      values={formik.values}
                      handleChange={formik.handleChange}
                      setFieldValue={formik.setFieldValue}
                      touched={formik.touched}
                      errors={formik.errors}
                    />
                  )}

                  {activeStep === 1 && (
                    <Variations
                      values={formik.values}
                      onChange={(items) => {
                        formik.setFieldValue("variations", items);
                      }}
                    />
                  )}

                  {activeStep === 2 && (
                    <TagSelection
                      values={formik.values}
                      handleChange={formik.handleChange}
                      setFieldValue={formik.setFieldValue}
                      touched={formik.touched}
                      errors={formik.errors}
                    />
                  )}
                  {activeStep === 3 && (
                    <Seo
                      values={formik.values}
                      handleChange={formik.handleChange}
                      setFieldValue={formik.setFieldValue}
                      touched={formik.touched}
                      errors={formik.errors}
                    />
                  )}
                  
                  {/* Navigation buttons */}
                  <Box mt={4} display="flex" justifyContent="space-between">
                    <Button
                      type="button"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      variant="outlined"
                    >
                      Back
                    </Button>

                    {activeStep < steps.length - 1 ? (
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        onClick={(e) => handleNext(e, formik.values, formik)}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={formik.isSubmitting}
                      >
                        {isEdit ? "Update Product" : "Create Product"}
                      </Button>
                    )}
                  </Box>
                </Form>
              )}
            </Formik>
          )}
        </Paper>
      </Container>
    </DashboardLayout>
  );
}

export default AddProduct;